import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, Tooltip, Skeleton } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';

interface SummaryCardProps {
  title: string;
  amount: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'info';
  changeDescription?: string;
  icon?: React.ReactNode;
  textureImagePath: string;
  isLoading?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, amount, change, changeType, changeDescription, icon, textureImagePath, isLoading }) => {
  const theme = useTheme();
  const isPositive = changeType === 'positive';

  const changeColor = changeType === 'positive' || changeType === 'info' ? 'rgba(3, 166, 90, 1)' : theme.palette.error.main;

   let formattedChange = change;
  if (change) {
    // 1. This regex now removes ALL leading '+' or '-' signs, fixing the double-sign issue.
    const valueWithoutSign = String(change).replace(/^[+-]+/, '');

    // 2. Add the correct sign based on arrow direction.
    if (changeType === 'negative') { // Up Arrow
      formattedChange = `-${valueWithoutSign}`;
    } else if (changeType === 'positive') { // Down Arrow
      formattedChange = `+${valueWithoutSign}`;
    }
  }

  const ChangeIndicatorIcon = () => {
    if (changeType === 'info') {
      return <AccountBalanceWalletOutlinedIcon sx={{ fontSize: '0.80rem', mr: 0.5, color: changeColor }} />;
    }
    
    const ChangeArrowComponent = isPositive ? ArrowDownward : ArrowUpward;
    const rotationTransform = isPositive ? 'rotate(120deg)' : 'rotate(-120deg)';

    return (
      <ChangeArrowComponent
        sx={{
          fontSize: '1rem',
          // mr: 0.5,
          color: changeColor,
          transform: rotationTransform,
          transition: 'transform 0.3s ease',
        }}
      />
    );
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '15px',
        height: '100%',
        backgroundColor: '#e3f2fd',
        position: 'relative',
        ...(textureImagePath && {
          backgroundImage: `url(${process.env.PUBLIC_URL + textureImagePath})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover'
        }),
      }}
    >
      <CardContent 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          // mt:'2px', 
          pb: 2, 
          '&:last-child': { 
            pb: 2 
          }
        }}
      >        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
          <Typography  color=' #666666' sx={{ fontWeight: '400', fontFamily: 'Inter',fontSize:'14px',mb:'10px' }}>{title}</Typography>
          {icon && (
            <Box sx={{ color: 'text.secondary' }}>{icon}</Box>
          )}
        </Box>

        {isLoading ? (
          <Skeleton variant="text" width="80%" height={32} sx={{ mb: 1 }} />
        ) : (
          <Tooltip title={amount} arrow>
            <Typography 
              component="p" 
              sx={{ 
                fontFamily: 'Inter',
                fontSize:'22px', 
                fontWeight: '600', 
                mb: 1,
                lineHeight:'24px', 
                color: '#333333',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {/* {amount} */}
              {amount && amount.startsWith('$') ? `${amount.charAt(0)}\u00A0${amount.slice(1)}` : amount}

            </Typography>
          </Tooltip>
        )}

        {isLoading ? (
          <Skeleton variant="rounded" width={150} height={24} />
        ) : (
          change && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: theme.palette.common.white,
                  borderRadius: '15px',
                  px: 0.50,
                  py: 0.25,
                  // We limit the max-width to prevent it from growing too wide
                  maxWidth: '100%',
                }}
              >
                <ChangeIndicatorIcon />
                <Typography variant="caption" sx={{ color: changeColor, fontWeight: 'medium', lineHeight: 'normal' }}>
                  {/* {change} */}
                                    {formattedChange}

                </Typography>
                
                {/* --- START OF MODIFICATION --- */}
                {changeDescription && (
                  <Tooltip title={changeDescription} arrow>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        ml: 0.75, 
                        lineHeight: 'normal',
                        // Add these three lines to handle overflow
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      • {changeDescription}
                    </Typography>
                  </Tooltip>
                )}
                {/* --- END OF MODIFICATION --- */}
              </Box>
            </Box>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default SummaryCard;